import React, { useState, useRef, useCallback } from 'react';
import { useNotifications } from './NotificationContext';
import NotificationPanel from './NotificationPanel';
import styles from './notifications.module.scss';

const BellSvg: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

interface NotificationBellProps {
  /** Optional controlled-open state. Falls back to internal state if omitted. */
  open?: boolean;
  /** Called when the user toggles the panel. Receives the new desired state. */
  onOpenChange?: (next: boolean) => void;
  /** "up" for bells that live in a bottom bar, so the panel opens upward. */
  placement?: "down" | "up";
  /** Drops the trailing gutter for bells inside an evenly-spaced bar. */
  compact?: boolean;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  open,
  onOpenChange,
  placement = "down",
  compact = false,
}) => {
  const { unreadCount, refresh } = useNotifications();
  const [internalOpen, setInternalOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isControlled = open !== undefined;
  const panelOpen = isControlled ? !!open : internalOpen;

  // Clamp background scroll on mobile without the heavy body pin — that pin
  // froze the sheet's inner touch-scroll and pushed the fixed nav off-screen.
  // We rely on the sheet's fixed layout + panelBody overscroll-behavior instead.
  // (Scroll lock removed — overflow:hidden on body was still blocking nested
  // touch-scroll on some iOS builds even with the light lock.)

  const setOpen = useCallback(
    (next: boolean) => {
      if (isControlled) {
        onOpenChange?.(next);
      } else {
        setInternalOpen(next);
        onOpenChange?.(next);
      }
    },
    [isControlled, onOpenChange],
  );

  const togglePanel = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const next = !panelOpen;
      setOpen(next);
      if (next) refresh();
    },
    [panelOpen, refresh, setOpen],
  );

  const closePanel = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <div
      className={`${styles.bellWrapper}${compact ? ` ${styles.bellWrapperCompact}` : ''}`}
      ref={wrapperRef}
      onClick={(e) => e.stopPropagation()} // isolate bell from parent click handlers
    >
      {/* A real button, not a div with role="button": the div took focus but
          only listened for clicks, so Enter and Space did nothing and the panel
          was mouse-only. The count goes in the name because the badge is
          decorative to a screen reader. */}
      <button
        type="button"
        className={styles.bellButton}
        onClick={togglePanel}
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : 'Notifications'
        }
        aria-expanded={panelOpen}
        aria-haspopup="dialog"
      >
        <BellSvg className={styles.bellIcon} />
        {unreadCount > 0 && (
          <span className={styles.badge} aria-hidden="true">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {panelOpen && <NotificationPanel onClose={closePanel} placement={placement} />}
    </div>
  );
};

export default NotificationBell;
